import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { countries } from 'countries-list';
import { useLanguage } from '../i18n/LanguageContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
`;

const Header = styled.header`
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 16px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.h1`
  font-size: 24px;
  color: #0c4da2;
  margin: 0;
  cursor: pointer;
`;

const MainContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Section = styled.section`
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  color: #1a202c;
  margin-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 10px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 16px;
  &:focus {
    outline: none;
    border-color: #3182ce;
    box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  color: #2d3748;
  cursor: pointer;
`;

const Button = styled.button`
  background: #3182ce;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #2c5282;
  }

  &:disabled {
    background: #cbd5e0;
    cursor: not-allowed;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const MultiSelect = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 16px;
  min-height: 150px;
`;

const AdminSettingsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [settings, setSettings] = useState({
    transport_types: {
      road: { rate_per_km: 1.5, rate_per_tonne: 50, fixed_fees: 100 },
      sea: { rate_per_km: 0.8, rate_per_tonne: 30, fixed_fees: 500 },
      air: { rate_per_km: 5.0, rate_per_tonne: 200, fixed_fees: 300 }
    },
    europe_settings: {
      enabled: true,
      multiplier: 1.0,
      fixed_fee_surcharge: 0
    },
    africa_settings: {
      enabled: true,
      multiplier: 1.2,
      fixed_fee_surcharge: 50,
      active_countries: []
    }
  });

  const [newTransportType, setNewTransportType] = useState('');

  const allCountries = Object.entries(countries).map(([code, data]) => ({
    code,
    name: data.name
  })).sort((a, b) => a.name.localeCompare(b.name));

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        // Merge with defaults to ensure structure exists
        setSettings(prev => ({
          ...prev,
          ...data.data,
          transport_types: { ...prev.transport_types, ...(data.data.transport_types || {}) },
          europe_settings: { ...prev.europe_settings, ...(data.data.europe_settings || {}) },
          africa_settings: { ...prev.africa_settings, ...(data.data.africa_settings || {}) }
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage(t('pages.adminSettings.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleAddTransportType = () => {
    if (!newTransportType.trim()) return;
    const key = newTransportType.toLowerCase().replace(/\s+/g, '_');
    
    if (settings.transport_types[key]) {
      alert('This transport type already exists!');
      return;
    }

    setSettings(prev => ({
      ...prev,
      transport_types: {
        ...prev.transport_types,
        [key]: { rate_per_km: 0, rate_per_tonne: 0, fixed_fees: 0 }
      }
    }));
    setNewTransportType('');
  };

  const handleDeleteTransportType = (type) => {
    if (window.confirm(`Are you sure you want to delete ${type}?`)) {
      setSettings(prev => {
        const newTypes = { ...prev.transport_types };
        delete newTypes[type];
        return { ...prev, transport_types: newTypes };
      });
    }
  };

  const handleTransportChange = (type, field, value) => {
    setSettings(prev => ({
      ...prev,
      transport_types: {
        ...prev.transport_types,
        [type]: {
          ...prev.transport_types[type],
          [field]: parseFloat(value) || 0
        }
      }
    }));
  };

  const handleRegionChange = (region, field, value) => {
    setSettings(prev => ({
      ...prev,
      [region]: {
        ...prev[region],
        [field]: value
      }
    }));
  };

  const handleAfricaCountriesChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setSettings(prev => ({
      ...prev,
      africa_settings: {
        ...prev.africa_settings,
        active_countries: selectedOptions
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      const data = await response.json();
      if (data.success) {
        setMessage(t('pages.adminSettings.saved'));
      } else {
        setMessage(t('pages.adminSettings.saveFailed'));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage(t('pages.adminSettings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageContainer>{t('pages.adminSettings.loading')}</PageContainer>;

  return (
    <PageContainer>
      <Header>
        <Logo onClick={() => navigate('/admin')}>{t('pages.adminDashboard.logo')}</Logo>
        <Button onClick={() => navigate('/admin')} style={{ background: 'transparent', color: '#4a5568', border: '1px solid #cbd5e0' }}>
          {t('pages.adminSettings.back')}
        </Button>
      </Header>

      <MainContent>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h2 style={{ fontSize: '28px', color: '#1a202c' }}>{t('pages.adminSettings.title')}</h2>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('pages.adminSettings.saving') : t('pages.adminSettings.save')}
          </Button>
        </div>

        {message && (
          <div style={{ 
            padding: '15px', 
            background: message.includes('success') ? '#c6f6d5' : '#fed7d7', 
            color: message.includes('success') ? '#2f855a' : '#c53030',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {message}
          </div>
        )}

        <Section>
          <SectionTitle>{t('pages.adminSettings.transportTitle')}</SectionTitle>
          
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <Input 
              placeholder={t('pages.adminSettings.newTypePlaceholder')} 
              value={newTransportType}
              onChange={(e) => setNewTransportType(e.target.value)}
              style={{ maxWidth: '300px' }}
            />
            <Button onClick={handleAddTransportType} style={{ background: '#48bb78' }}>
              {t('pages.adminSettings.addType')}
            </Button>
          </div>

          <Grid>
            {Object.keys(settings.transport_types).map(type => (
              <div key={type} style={{ background: '#f7fafc', padding: '15px', borderRadius: '8px', position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ textTransform: 'capitalize', margin: 0 }}>{type.replace(/_/g, ' ')}</h4>
                  <button 
                    onClick={() => handleDeleteTransportType(type)}
                    style={{ background: 'transparent', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '18px' }}
                    title={t('pages.adminSettings.deleteType')}
                  >
                    ×
                  </button>
                </div>
                <FormGroup>
                  <Label>{t('pages.adminSettings.rateKm')}</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={settings.transport_types[type]?.rate_per_km || 0}
                    onChange={(e) => handleTransportChange(type, 'rate_per_km', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>{t('pages.adminSettings.rateTonne')}</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={settings.transport_types[type]?.rate_per_tonne || 0}
                    onChange={(e) => handleTransportChange(type, 'rate_per_tonne', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>{t('pages.adminSettings.fixedFees')}</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={settings.transport_types[type]?.fixed_fees || 0}
                    onChange={(e) => handleTransportChange(type, 'fixed_fees', e.target.value)}
                  />
                </FormGroup>
              </div>
            ))}
          </Grid>
        </Section>

        <Section>
          <SectionTitle>{t('pages.adminSettings.europeTitle')}</SectionTitle>
          <FormGroup>
            <CheckboxLabel>
              <input 
                type="checkbox"
                checked={settings.europe_settings.enabled}
                onChange={(e) => handleRegionChange('europe_settings', 'enabled', e.target.checked)}
              />
              {t('pages.adminSettings.europeEnable')}
            </CheckboxLabel>
          </FormGroup>
          <Grid>
            <FormGroup>
              <Label>{t('pages.adminSettings.multiplier')}</Label>
              <Input 
                type="number" 
                step="0.1"
                value={settings.europe_settings.multiplier}
                onChange={(e) => handleRegionChange('europe_settings', 'multiplier', parseFloat(e.target.value))}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t('pages.adminSettings.surcharge')}</Label>
              <Input 
                type="number" 
                step="1"
                value={settings.europe_settings.fixed_fee_surcharge}
                onChange={(e) => handleRegionChange('europe_settings', 'fixed_fee_surcharge', parseFloat(e.target.value))}
              />
            </FormGroup>
          </Grid>
        </Section>

        <Section>
          <SectionTitle>{t('pages.adminSettings.africaTitle')}</SectionTitle>
          <FormGroup>
            <CheckboxLabel>
              <input 
                type="checkbox"
                checked={settings.africa_settings.enabled}
                onChange={(e) => handleRegionChange('africa_settings', 'enabled', e.target.checked)}
              />
              {t('pages.adminSettings.africaEnable')}
            </CheckboxLabel>
          </FormGroup>
          <Grid>
            <FormGroup>
              <Label>{t('pages.adminSettings.multiplier')}</Label>
              <Input 
                type="number" 
                step="0.1"
                value={settings.africa_settings.multiplier}
                onChange={(e) => handleRegionChange('africa_settings', 'multiplier', parseFloat(e.target.value))}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t('pages.adminSettings.surcharge')}</Label>
              <Input 
                type="number" 
                step="1"
                value={settings.africa_settings.fixed_fee_surcharge}
                onChange={(e) => handleRegionChange('africa_settings', 'fixed_fee_surcharge', parseFloat(e.target.value))}
              />
            </FormGroup>
          </Grid>
          <FormGroup style={{ marginTop: '20px' }}>
            <Label>{t('pages.adminSettings.activeCountries')}</Label>
            <MultiSelect 
              multiple 
              value={settings.africa_settings.active_countries || []}
              onChange={handleAfricaCountriesChange}
            >
              {allCountries.filter(c => c.code !== 'EU').map(country => (
                <option key={country.code} value={country.name}>
                  {country.name}
                </option>
              ))}
            </MultiSelect>
            <small style={{ color: '#718096', display: 'block', marginTop: '5px' }}>
              {t('pages.adminSettings.selectHint')}
            </small>
          </FormGroup>
        </Section>

      </MainContent>
    </PageContainer>
  );
};

export default AdminSettingsPage;
